import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { SETTINGS_ROUTES } from "@/shared/constants/routes";
import { Outlet, useLocation } from "react-router-dom";
import { useQuestionnaireWizardStore } from "../../states/questionnaire-wizard";
import { useSettingsHeaderStore } from "../../states/settings-header";
import CreateQuestionnaireScreen from "../create-questionnaire-screen";
import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states/index.ts";

const SettingsLayout = () => {
  const location = useLocation();
  const { isWizardOpen, questionnaireId, openWizard, closeWizard } = useQuestionnaireWizardStore();
  const { handleSave, isSaving, isDisabled } = useSettingsHeaderStore();

  // Check if user is admin
  const member = useLoggedInMember();
  const memberId = member?.member_id || "";
  const { isTeamOrgAdmin, isTeamAdmin } = member ? getZaniaTeamMemberRoles(memberId, member) : { isTeamOrgAdmin: false, isTeamAdmin: false };
  const isAdmin = isTeamOrgAdmin || isTeamAdmin;

  // If questionnaire wizard is open, render it as full-screen overlay
  if (isWizardOpen) {
    return (
      <div
        className="fixed inset-0 z-50 p-4"
        style={{
          background: `linear-gradient(
            180deg,
            #f4f4f5 0%,
            #f8e3ea 23.5%,
            #fdd0df 47.5%,
            #efd2ea 73%,
            #e4d4f4 100%
          )`
        }}
      >
        <CreateQuestionnaireScreen onClose={closeWizard} questionnaireId={questionnaireId || undefined} />
      </div>
    );
  }

  // Determine breadcrumb items based on current route
  const getBreadcrumbItems = () => {
    const path = location.pathname;

    if (path.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "" },
        { title: "Questionnaire & Document Sets", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS)) {
      return [
        { title: "Third-Party Risk Agent Settings", href: "" },
        { title: "Control Sets", href: "" }
      ];
    }

    if (path.includes(SETTINGS_ROUTES.INTAKE_FORM)) {
      return [
        { title: "Third-Party Risk Assessment Settings", href: "" },
        { title: "Intake Form", href: "" }
      ];
    }

    return [{ title: "Settings", href: "/settings" }];
  };

  // Determine if we should show Create New button
  const shouldShowCreateButton = () => {
    const path = location.pathname;

    return path.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES) ||
      path.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS);
  };

  // Handle Create New button click
  const handleCreateNew = () => {
    if (location.pathname.includes(SETTINGS_ROUTES.THIRD_PARTY_QUESTIONNAIRES)) {
      window.dispatchEvent(new CustomEvent('openCreateQuestionnaireModal'));
    } else if (location.pathname.includes(SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS)) {
      window.dispatchEvent(new CustomEvent('openCreateControlSetModal'));
    }
  };

  // Check if we're on the intake form route
  const isIntakeFormRoute = location.pathname.includes(SETTINGS_ROUTES.INTAKE_FORM);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
        <div className="flex gap-4 items-center py-1.5">
          <ZBreadCrumbs items={getBreadcrumbItems()} />
        </div>
        {/* Show Done button for intake form route */}
        {isIntakeFormRoute && handleSave && (
          <div className="flex items-center gap-4">
            <ZButton
              color="primary"
              onClick={() => void handleSave()}
              isDisabled={isDisabled}
              isLoading={isSaving}
              className="px-3 py-1 h-auto mt-0.5"
            >
              Done
            </ZButton>
          </div>
        )}
        {/* Show Create New button for other routes */}
        {shouldShowCreateButton() && isAdmin && (
          <div className="flex gap-2">
            <ZButton
              color="primary"
              className="text-xs font-medium h-7 px-3"
              onClick={handleCreateNew}
            >
              Create New
            </ZButton>
          </div>
        )}
      </div>
      <div className="grow bg-white overflow-hidden rounded-lg">
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
